import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, styled } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { Button } from '@libComponents';
import { apiClient } from '@libs/apiRequest';
import { getCookie } from '@libs/cookies';
import { AUTH_TOKEN_KEY } from 'auth/common';

/**
 * Secure document viewer (route: /file-viewer?src=<stored file path>).
 *
 * Employee documents (photograph, signature, NID, leave attachments, …) live
 * behind `file/private/*`, which needs the Bearer token and per-file
 * permissions — so their links can't be plain URLs. Exports (PDF "OPEN"
 * links, Excel link columns) point here instead: this page fetches the file
 * through the API with the logged-in user's credentials and renders it.
 */

const Wrap = styled(Box)(({ theme: { spacing, palette } }) => ({
    minHeight: '70vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing(2),
    padding: spacing(3),
    textAlign: 'center',

    '& img': {
        maxWidth: 'min(920px, 100%)',
        maxHeight: '70vh',
        borderRadius: 8,
        border: `1px solid ${palette.divider}`,
        background: palette.background.paper,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
    },

    '& iframe': {
        width: 'min(920px, 100%)',
        height: '75vh',
        border: `1px solid ${palette.divider}`,
        borderRadius: 8,
        background: palette.background.paper
    }
}));

export default function FileViewer() {
    const [searchParams] = useSearchParams();
    const src = searchParams.get('src') || '';
    const filename = decodeURIComponent(
        (src.split('/').pop() || 'document').split('?')[0]
    );

    const [state, setState] = useState({ status: src ? 'loading' : 'error' });

    useEffect(() => {
        if (!src) return undefined;
        let objectUrl = null;
        let cancelled = false;

        const token = getCookie(AUTH_TOKEN_KEY);
        apiClient
            .get(src, {
                responseType: 'blob',
                ...(token
                    ? { headers: { Authorization: `Bearer ${token}` } }
                    : {})
            })
            .then((resp) => {
                if (cancelled) return;
                objectUrl = window.URL.createObjectURL(resp.data);
                setState({
                    status: 'ready',
                    url: objectUrl,
                    mime: resp.data?.type || ''
                });
            })
            .catch((error) => {
                if (cancelled) return;
                setState({
                    status: 'error',
                    message:
                        error?.response?.status === 403
                            ? 'You do not have permission to view this document.'
                            : 'This document could not be loaded. It may have been removed.'
                });
            });

        return () => {
            cancelled = true;
            if (objectUrl) window.URL.revokeObjectURL(objectUrl);
        };
    }, [src]);

    if (state.status === 'loading') {
        return (
            <Wrap>
                <CircularProgress size={40} />
                <Typography variant='body2' color='text.secondary'>
                    Loading secure document…
                </Typography>
            </Wrap>
        );
    }

    if (state.status === 'error') {
        return (
            <Wrap>
                <ErrorOutlineIcon color='error' sx={{ fontSize: 44 }} />
                <Typography variant='h6'>Document unavailable</Typography>
                <Typography variant='body2' color='text.secondary'>
                    {state.message || 'No document was specified.'}
                </Typography>
            </Wrap>
        );
    }

    const isImage = state.mime.startsWith('image/');
    const isPdf = state.mime === 'application/pdf';

    return (
        <Wrap>
            <Typography variant='h6' sx={{ wordBreak: 'break-all' }}>
                {filename}
            </Typography>

            {isImage && <img src={state.url} alt={filename} />}
            {isPdf && <iframe title={filename} src={state.url} />}
            {!isImage && !isPdf && (
                <Typography variant='body2' color='text.secondary'>
                    Preview is not available for this file type — use the
                    download button below.
                </Typography>
            )}

            <Button
                startIcon={<FileDownloadOutlinedIcon />}
                sx={{ width: 'auto', px: 3 }}
                onClick={() => {
                    const a = document.createElement('a');
                    a.href = state.url;
                    a.download = filename;
                    a.click();
                }}
            >
                Download
            </Button>
        </Wrap>
    );
}
